import _ from "lodash";
import { createContext, useContext, useMemo, useReducer, memo, useCallback } from "react";
import { FilterState } from "./state-models";
import { PropertyValue } from "../shared/models/metadata.model";
import { SearchOptions } from "../shared/models/search-options.model";
import { urlQueryKeys } from "./state-url-management";
import { deepFreeze } from "../shared/utils/freeze.utils";
import { useSiteContext } from "./SiteContext";

export enum FilterActionName {
    REMOVE_CUSTOM_PROPERTY = "REMOVE_CUSTOM_PROPERTY",
    REMOVE_SYSTEM_PROPERTY = "REMOVE_SYSTEM_PROPERTY",
    SET_CUSTOM_PROPERTY = "SET_CUSTOM_PROPERTY",
    SET_SYSTEM_PROPERTY = "SET_SYSTEM_PROPERTY",
    SET_STATE = "SET_STATE",
    UPDATE_CUSTOM_PROPERTIES = "UPDATE_CUSTOM_PROPERTIES",
    UPDATE_SYSTEM_PROPERTIES = "UPDATE_SYSTEM_PROPERTIES",
    UPDATE_DISPLAY_PROPERTIES = "UPDATE_DISPLAY_PROPERTIES",
    UPDATE_VALIDITY = "UPDATE_VALIDITY",
    UPDATE_LANG = "UPDATE_LANG",
    UPDATE_LOCAL_SEARCH = "UPDATE_LOCAL_SEARCH",
    UPDATE_TEXT = "UPDATE_TEXT",
    UPDATE_SEMANTIC = "UPDATE_SEMANTIC",
    UPDATE_SEARCH_OPTIONS = "UPDATE_SEARCH_OPTIONS",
    UPDATE_BASELINE = "UPDATE_BASELINE",
    UPDATE_ORGANIZATION = "UPDATE_ORGANIZATION",
    UPDATE_CONFIGURATION = "UPDATE_CONFIGURATION",
    UPDATE_CONTENT_TYPE = "UPDATE_CONTENT_TYPE",
    UPDATE_CASE_SENSITIVE_SEARCH = "UPDATE_CASE_SENSITIVE_SEARCH",
    UPDATE_WHOLE_WORD_SEARCH = "UPDATE_WHOLE_WORD_SEARCH",
}

export type FilterStateAction =
    | { type: FilterActionName.REMOVE_CUSTOM_PROPERTY; payload: string }
    | { type: FilterActionName.REMOVE_SYSTEM_PROPERTY; payload: string }
    | { type: FilterActionName.SET_CUSTOM_PROPERTY; payload: { name: string, value: PropertyValue[] } }
    | { type: FilterActionName.SET_SYSTEM_PROPERTY; payload: { name: string, value: PropertyValue[] } }
    | { type: FilterActionName.SET_STATE; payload: FilterState }
    | { type: FilterActionName.UPDATE_CUSTOM_PROPERTIES; payload: FilterState["customProperties"] }
    | { type: FilterActionName.UPDATE_SYSTEM_PROPERTIES; payload: FilterState["systemProperties"] }
    | { type: FilterActionName.UPDATE_DISPLAY_PROPERTIES; payload: FilterState["displayProperties"] }
    | { type: FilterActionName.UPDATE_VALIDITY; payload: FilterState["validity"] }
    | { type: FilterActionName.UPDATE_LANG; payload: FilterState["lang"] }
    | { type: FilterActionName.UPDATE_LOCAL_SEARCH; payload: FilterState["localSearch"] }
    | { type: FilterActionName.UPDATE_TEXT; payload: FilterState["text"] }
    | { type: FilterActionName.UPDATE_SEMANTIC; payload: FilterState["semantic"] }
    | { type: FilterActionName.UPDATE_SEARCH_OPTIONS; payload: FilterState["searchOptions"] }
    | { type: FilterActionName.UPDATE_BASELINE; payload: FilterState["baseline"] }
    | { type: FilterActionName.UPDATE_ORGANIZATION; payload: FilterState["organization"] }
    | { type: FilterActionName.UPDATE_CONFIGURATION; payload: FilterState["configuration"] }
    | { type: FilterActionName.UPDATE_CONTENT_TYPE; payload: FilterState["contentType"] }
    | { type: FilterActionName.UPDATE_CASE_SENSITIVE_SEARCH; payload: FilterState["caseSensitiveSearch"] }
    | { type: FilterActionName.UPDATE_WHOLE_WORD_SEARCH; payload: FilterState["wholeWordSearch"] };

export const filterStateReducer = (state: FilterState, action: FilterStateAction): FilterState => {
    let newState = _.cloneDeep(state);
    switch (action.type) {
        case FilterActionName.REMOVE_CUSTOM_PROPERTY:
            newState = { ...state, customProperties: _.omit(state.customProperties, action.payload) };
            break;
        case FilterActionName.REMOVE_SYSTEM_PROPERTY:
            newState = { ...state, systemProperties: _.omit(state.systemProperties, action.payload) };
            break;
        case FilterActionName.SET_CUSTOM_PROPERTY:
            newState = { ...state, customProperties: { ...state.customProperties, [action.payload.name]: action.payload.value } };
            break;
        case FilterActionName.SET_STATE: {
            const previousText = state.text;
            newState = _.cloneDeep(action.payload);
            if (previousText !== newState.text) {
                newState = { ...newState, localSearch: newState.text };
            }
            break;
        }
        case FilterActionName.SET_SYSTEM_PROPERTY:
            newState = { ...state, systemProperties: { ...state.systemProperties, [action.payload.name]: action.payload.value } };
            break;
        case FilterActionName.UPDATE_CUSTOM_PROPERTIES:
            newState = _.isEqual(state.customProperties, action.payload) ? state : { ...state, customProperties: action.payload };
            break;
        case FilterActionName.UPDATE_SYSTEM_PROPERTIES:
            newState = _.isEqual(state.systemProperties, action.payload) ? state : { ...state, systemProperties: action.payload };
            break;
        case FilterActionName.UPDATE_DISPLAY_PROPERTIES:
            newState = _.isEqual(state.displayProperties, action.payload) ? state : { ...state, displayProperties: action.payload };
            break;
        case FilterActionName.UPDATE_VALIDITY:
            newState = state.validity === action.payload ? state : { ...state, validity: action.payload };
            break;
        case FilterActionName.UPDATE_LANG:
            newState = state.lang === action.payload ? state : { ...state, lang: action.payload };
            break;
        case FilterActionName.UPDATE_LOCAL_SEARCH:
            newState = state.localSearch === action.payload ? state : { ...state, localSearch: action.payload };
            break;
        case FilterActionName.UPDATE_TEXT:
            if (state.text === action.payload) {
                newState = state;
            }
            else {
                newState = { ...state, text: action.payload, localSearch: action.payload };
            }
            break;
        case FilterActionName.UPDATE_SEMANTIC:
            newState = state.semantic === action.payload ? state : { ...state, semantic: action.payload };
            break;
        case FilterActionName.UPDATE_SEARCH_OPTIONS:
            newState = _.isEqual(state.searchOptions, action.payload) ? state : { ...state, searchOptions: action.payload };
            break;
        case FilterActionName.UPDATE_BASELINE:
            newState = state.baseline === action.payload ? state : { ...state, baseline: action.payload };
            break;
        case FilterActionName.UPDATE_ORGANIZATION:
            newState = state.organization === action.payload ? state : { ...state, organization: action.payload };
            break;
        case FilterActionName.UPDATE_CONFIGURATION:
            newState = state.configuration === action.payload ? state : { ...state, configuration: action.payload };
            break;
        case FilterActionName.UPDATE_CONTENT_TYPE:
            newState = state.contentType === action.payload ? state : { ...state, contentType: action.payload };
            break;
        case FilterActionName.UPDATE_CASE_SENSITIVE_SEARCH:
            newState = state.caseSensitiveSearch === action.payload ? state : { ...state, caseSensitiveSearch: action.payload };
            break;
        case FilterActionName.UPDATE_WHOLE_WORD_SEARCH:
            newState = state.wholeWordSearch === action.payload ? state : { ...state, wholeWordSearch: action.payload };
            break;
        default:
            break;
    }
    if (!_.isEqual(newState, state)) {
        return newState;
    }
    return state;
};

type FilterContextType = {
    filterState: FilterState;
    actions: Record<FilterActionName, (...args: any[]) => void>;
    rootState: FilterState;
} | null;

type FilterDispatchContextType = {
    dispatch: React.Dispatch<FilterStateAction>;
    createBranch: (initialState: FilterState) => React.ComponentType<{ children: React.ReactNode }>;
};

export const FilterStateContext = createContext<FilterContextType | undefined>(undefined);
export const FilterDispatchContext = createContext<FilterDispatchContextType | undefined>(undefined);

type FilterStateProviderProps = {
    children: React.ReactNode;
    initialState?: FilterState;
    parentDispatch?: React.Dispatch<FilterStateAction>;
};

// This component is used to provide the filter state to the component tree
export const FilterStateProvider = memo(({ children, initialState = {}, parentDispatch }: FilterStateProviderProps) => {

    const { siteState: _siteState } = useSiteContext();

    // Memoize the initial state to prevent unnecessary re-renders
    const memoizedInitialState = useMemo(() => {
        return initialState;
    }, [initialState]);
    const parentContext = useContext(FilterStateContext);
    const [localState, dispatch] = useReducer(filterStateReducer, memoizedInitialState);

    // Merge parent and local state, with local state taking precedence
    // The merged state is used as the source of truth for the state
    const mergedState = useMemo(() => {
        const parentState = parentContext?.filterState || {};
        const result = {
            ...parentState,
            ...localState,
        };
        if (process.env.NODE_ENV !== 'production') {
            // Freeze the state in development mode to catch accidental mutation
            deepFreeze(result);
        }
        return result;
    }, [parentContext?.filterState, localState]);

    // Root state is the state at the topmost level (no parent)
    // For the root provider, this is the localState (which equals mergedState)
    // For nested providers, we use the parent's rootState
    const rootState = useMemo(() => {
        if (!parentContext) {
            // This is the root provider, so rootState is the localState
            return localState;
        }
        // Use the parent's rootState
        return parentContext.rootState;
    }, [parentContext, localState]);

    // Create actions for updating the state
    // These actions are used to update the state in the current state branch
    const actions: Record<FilterActionName, (...args: any[]) => void> = useMemo(() => {
        return {
            [FilterActionName.REMOVE_CUSTOM_PROPERTY]: (name: string) => dispatch({ type: FilterActionName.REMOVE_CUSTOM_PROPERTY, payload: name }),
            [FilterActionName.REMOVE_SYSTEM_PROPERTY]: (name: string) => dispatch({ type: FilterActionName.REMOVE_SYSTEM_PROPERTY, payload: name }),
            [FilterActionName.SET_CUSTOM_PROPERTY]: (name: string, value: PropertyValue[]) => dispatch({ type: FilterActionName.SET_CUSTOM_PROPERTY, payload: { name, value } }),
            [FilterActionName.SET_SYSTEM_PROPERTY]: (name: string, value: PropertyValue[]) => dispatch({ type: FilterActionName.SET_SYSTEM_PROPERTY, payload: { name, value } }),
            [FilterActionName.SET_STATE]: (state: FilterState) => dispatch({ type: FilterActionName.SET_STATE, payload: state }),
            [FilterActionName.UPDATE_CUSTOM_PROPERTIES]: (props: Record<string, PropertyValue[]>) => dispatch({ type: FilterActionName.UPDATE_CUSTOM_PROPERTIES, payload: props }),
            [FilterActionName.UPDATE_SYSTEM_PROPERTIES]: (props: Record<string, PropertyValue[]>) => dispatch({ type: FilterActionName.UPDATE_SYSTEM_PROPERTIES, payload: props }),
            [FilterActionName.UPDATE_DISPLAY_PROPERTIES]: (props: Record<string, PropertyValue[]>) => dispatch({ type: FilterActionName.UPDATE_DISPLAY_PROPERTIES, payload: props }),
            [FilterActionName.UPDATE_VALIDITY]: (props: string) => dispatch({ type: FilterActionName.UPDATE_VALIDITY, payload: props }),
            [FilterActionName.UPDATE_LANG]: (props: string) => dispatch({ type: FilterActionName.UPDATE_LANG, payload: props }),
            [FilterActionName.UPDATE_LOCAL_SEARCH]: (props: string) => dispatch({ type: FilterActionName.UPDATE_LOCAL_SEARCH, payload: props }),
            [FilterActionName.UPDATE_TEXT]: (props: string) => dispatch({ type: FilterActionName.UPDATE_TEXT, payload: props }),
            [FilterActionName.UPDATE_SEMANTIC]: (props: string) => dispatch({ type: FilterActionName.UPDATE_SEMANTIC, payload: props }),
            [FilterActionName.UPDATE_SEARCH_OPTIONS]: (props: SearchOptions) => dispatch({ type: FilterActionName.UPDATE_SEARCH_OPTIONS, payload: props }),
            [FilterActionName.UPDATE_BASELINE]: (props: string) => dispatch({ type: FilterActionName.UPDATE_BASELINE, payload: props }),
            [FilterActionName.UPDATE_ORGANIZATION]: (props: string) => dispatch({ type: FilterActionName.UPDATE_ORGANIZATION, payload: props }),
            [FilterActionName.UPDATE_CONFIGURATION]: (props: string) => dispatch({ type: FilterActionName.UPDATE_CONFIGURATION, payload: props }),
            [FilterActionName.UPDATE_CONTENT_TYPE]: (props: string) => dispatch({ type: FilterActionName.UPDATE_CONTENT_TYPE, payload: props }),
            [FilterActionName.UPDATE_CASE_SENSITIVE_SEARCH]: (props: boolean) => dispatch({ type: FilterActionName.UPDATE_CASE_SENSITIVE_SEARCH, payload: props }),
            [FilterActionName.UPDATE_WHOLE_WORD_SEARCH]: (props: boolean) => dispatch({ type: FilterActionName.UPDATE_WHOLE_WORD_SEARCH, payload: props }),
        };
    }, [dispatch]);

    // Create a new state branch
    // The new state branch is a new instance of the ComponentStateProvider
    // It is used to hold state values for a component subtree
    const createBranch = useCallback((branchInitialState: FilterState) => {
        return ({ children }: { children: React.ReactNode }) => (
            <FilterStateProvider
                initialState={branchInitialState}
                parentDispatch={dispatch}
            >
                {children}
            </FilterStateProvider>
        );
    }, [dispatch]);

    return (
        <FilterStateContext.Provider value={{ filterState: mergedState, actions, rootState }}>
            <FilterDispatchContext.Provider value={{ dispatch: parentDispatch || dispatch, createBranch }}>
                {children}
            </FilterDispatchContext.Provider>
        </FilterStateContext.Provider>
    );
});

export const useFilterDispatch = () => {
    const context = useContext(FilterDispatchContext);
    if (!context) {
        throw new Error("useFilterDispatch must be used within a FilterStateProvider");
    }
    return context;
};

export const useFilterContext = () => {
    const context = useContext(FilterStateContext);
    if (!context) {
        throw new Error("useFilterContext must be used within a FilterStateProvider");
    }
    return context;
};

// Selector for state modifying actions
export const useFilterAction = (actionName: FilterActionName): ((...args: any[]) => void) => {
    const context = useContext(FilterStateContext);
    if (!context) {
        throw new Error('useFilterAction must be used within a FilterStateProvider');
    }
    return useCallback((...args: any[]) => context.actions[actionName](...args), [context.actions, actionName]);
}

// Selector for getting the createStateBranch function
export const useCreateFilterStateBranch = () => {
    const context = useContext(FilterDispatchContext);
    if (!context) {
        throw new Error('useFilterCreateStateBranch must be used within a FilterStateProvider');
    }
    return context.createBranch;
}

export const useFilterState = () => {
    const { filterState: mergedState } = useFilterContext();
    return mergedState;
};

export const useCustomPropertiesFilter = () => {
    const { filterState: mergedState } = useFilterContext();
    return useMemo(() => mergedState.customProperties || {}, [mergedState.customProperties]);
};

export const useCustomPropertyFilterValues = (name: string) => {
    const { filterState: mergedState } = useFilterContext();
    return useMemo(() => mergedState.customProperties?.[name || ""] || [], [mergedState.customProperties, name]);
};

export const useCustomPropertyFilterStringValue = (name: string) => {
    const { filterState: mergedState } = useFilterContext();
    return useMemo(() => mergedState.customProperties?.[name || ""]?.[0].toString().trim() || "", [mergedState.customProperties, name]);
};

export const useSystemPropertiesFilter = () => {
    const { filterState: mergedState } = useFilterContext();
    return useMemo(() => mergedState.systemProperties || {}, [mergedState.systemProperties]);
};

export const useSystemPropertyFilterValues = (name: string) => {
    const { filterState: mergedState } = useFilterContext();
    return useMemo(() => mergedState.systemProperties?.[name || ""] || [], [mergedState.systemProperties, name]);
};

export const useSystemPropertyFilterStringValue = (name: string) => {
    const { filterState: mergedState } = useFilterContext();
    return useMemo(() => mergedState.systemProperties?.[name || ""]?.[0].toString().trim() || "", [mergedState.systemProperties, name]);
};

export const useDisplayPropertiesFilter = () => {
    const { filterState: mergedState } = useFilterContext();
    return useMemo(() => mergedState.displayProperties || {}, [mergedState.displayProperties]);
};

export const useDisplayPropertyFilterValues = (name: string) => {
    const { filterState: mergedState } = useFilterContext();
    return useMemo(() => mergedState.displayProperties?.[name || ""] || [], [mergedState.displayProperties, name]);
};

export const useDisplayPropertyFilterStringValue = (name: string) => {
    const { filterState: mergedState } = useFilterContext();
    return useMemo(() => mergedState.displayProperties?.[name || ""]?.[0].toString().trim() || "", [mergedState.displayProperties, name]);
};

export const useValidityFilter = () => {
    const { filterState: mergedState } = useFilterContext();
    return useMemo(() => mergedState.validity || "", [mergedState.validity]);
};

export const useLangFilter = () => {
    const { filterState: mergedState } = useFilterContext();
    return useMemo(() => mergedState.lang || "", [mergedState.lang]);
};

export const useLocalSearchFilter = () => {
    const { filterState: mergedState } = useFilterContext();
    return useMemo(() => mergedState.localSearch || "", [mergedState.localSearch]);
};

export const useTextFilter = () => {
    const { filterState: mergedState } = useFilterContext();
    return useMemo(() => mergedState.text || "", [mergedState.text]);
};

export const useSemanticFilter = () => {
    const { filterState: mergedState } = useFilterContext();
    return useMemo(() => mergedState.semantic || "", [mergedState.semantic]);
};

export const useSearchOptionsFilter = () => {
    const { filterState: mergedState } = useFilterContext();
    return useMemo(() => mergedState.searchOptions || { order: [] }, [mergedState.searchOptions]);
};

export const useBaselineFilter = () => {
    const { filterState: mergedState } = useFilterContext();
    return useMemo(() => mergedState.baseline || "", [mergedState.baseline]);
};

export const useOrganizationFilter = () => {
    const { filterState: mergedState } = useFilterContext();
    return useMemo(() => mergedState.organization || "", [mergedState.organization]);
};

export const useConfigurationFilter = () => {
    const { filterState: mergedState } = useFilterContext();
    return useMemo(() => mergedState.configuration || "", [mergedState.configuration]);
};

export const useContentTypeFilter = () => {
    const { filterState: mergedState } = useFilterContext();
    return useMemo(() => mergedState.contentType || "", [mergedState.contentType]);
};

export const useCaseSensitiveSearch = () => {
    const { filterState: mergedState } = useFilterContext();
    return useMemo(() => mergedState.caseSensitiveSearch || false, [mergedState.caseSensitiveSearch]);
};

export const useWholeWordSearch = () => {
    const { filterState: mergedState } = useFilterContext();
    return useMemo(() => mergedState.wholeWordSearch || false, [mergedState.wholeWordSearch]);
};


/***
 * Removes specified parameters from the URL.
 * Each suppressable parameter is represented by a single character, defined
 * in urlQueryKeys.
 * The suppress argument is a string of characters, each representing a parameter
 * to suppress.
 */
export function useSuppressedFilter(suppress: string): FilterState {
    const { filterState: mergedState } = useFilterContext();
    return useMemo(() => {
        const linkedState: FilterState = Object.assign({}, mergedState);
        if (suppress === "*") {
            linkedState.systemProperties = {};
            linkedState.customProperties = {};
            linkedState.displayProperties = {};
            linkedState.searchOptions = { order: [] };
            delete linkedState.validity;
            delete linkedState.semantic;
            // delete linkedState.includes;
            delete linkedState.text;
            delete linkedState.organization;
            delete linkedState.configuration;
            delete linkedState.lang;
            delete linkedState.localSearch;
            delete linkedState.caseSensitiveSearch;
            delete linkedState.wholeWordSearch;
        }
        else {
            for (let spec of suppress) {
                switch (spec) {
                    case urlQueryKeys.systemProperties:
                        linkedState.systemProperties = {};
                        break;
                    case urlQueryKeys.validity:
                        delete linkedState.validity;
                        break;
                    case urlQueryKeys.lang:
                        delete linkedState.lang;
                        break;
                    case urlQueryKeys.customProperties:
                        linkedState.customProperties = {};
                        break;
                    case urlQueryKeys.textFilter:
                        delete linkedState.text;
                        break;
                    case urlQueryKeys.baseline:
                        delete linkedState.baseline;
                        break;
                    case urlQueryKeys.displayProperties:
                        linkedState.displayProperties = {};
                        break;
                    case urlQueryKeys.sortOrder:
                        linkedState.searchOptions = { order: [] };
                        break;
                    case urlQueryKeys.organization:
                        delete linkedState.organization;
                        break;
                    case urlQueryKeys.configuration:
                        delete linkedState.configuration;
                        break;
                    case urlQueryKeys.caseSensitiveSearch:
                        delete linkedState.caseSensitiveSearch;
                        break;
                    case urlQueryKeys.wholeWordSearch:
                        delete linkedState.wholeWordSearch;
                        break;
                    case urlQueryKeys.localSearch:
                        delete linkedState.localSearch;
                        break;
                }
            }
        }
        return linkedState;
    }, [mergedState, suppress]);
}

/***
 * Variant of useSuppressedFilter that uses the topmost root state instead of the current merged state.
 * This is useful when you want to suppress filters based on the root state rather than the current
 * merged state (which may include local overrides from nested providers). The root state reflects the
 * current state of the filter as it exists in the URL.
 */
export function useSuppressedFilterFromRoot(suppress: string): FilterState {
    const { rootState } = useFilterContext();
    return useMemo(() => {
        const linkedState: FilterState = Object.assign({}, rootState);
        if (suppress === "*") {
            linkedState.systemProperties = {};
            linkedState.customProperties = {};
            linkedState.displayProperties = {};
            linkedState.searchOptions = { order: [] };
            delete linkedState.validity;
            delete linkedState.semantic;
            // delete linkedState.includes;
            delete linkedState.text;
            delete linkedState.organization;
            delete linkedState.configuration;
            delete linkedState.lang;
            delete linkedState.localSearch;
            delete linkedState.caseSensitiveSearch;
            delete linkedState.wholeWordSearch;
        }
        else {
            for (let spec of suppress) {
                switch (spec) {
                    case urlQueryKeys.systemProperties:
                        linkedState.systemProperties = {};
                        break;
                    case urlQueryKeys.validity:
                        delete linkedState.validity;
                        break;
                    case urlQueryKeys.lang:
                        delete linkedState.lang;
                        break;
                    case urlQueryKeys.customProperties:
                        linkedState.customProperties = {};
                        break;
                    case urlQueryKeys.textFilter:
                        delete linkedState.text;
                        break;
                    case urlQueryKeys.baseline:
                        delete linkedState.baseline;
                        break;
                    case urlQueryKeys.displayProperties:
                        linkedState.displayProperties = {};
                        break;
                    case urlQueryKeys.sortOrder:
                        linkedState.searchOptions = { order: [] };
                        break;
                    case urlQueryKeys.organization:
                        delete linkedState.organization;
                        break;
                    case urlQueryKeys.configuration:
                        delete linkedState.configuration;
                        break;
                    case urlQueryKeys.caseSensitiveSearch:
                        delete linkedState.caseSensitiveSearch;
                        break;
                    case urlQueryKeys.wholeWordSearch:
                        delete linkedState.wholeWordSearch;
                        break;
                    case urlQueryKeys.localSearch:
                        delete linkedState.localSearch;
                        break;
                }
            }
        }
        return linkedState;
    }, [rootState, suppress]);
}